import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MB_Modal } from '@mightybyte/rnw.components.modal';
import {
    Dimensions,
    Linking,
    NativeScrollEvent,
    NativeSyntheticEvent,
    ScrollView,
    StyleSheet,
    Text,
    TouchableOpacity,
    View,
} from 'react-native';
import { trpc } from '../../../../../apiCalls/trpcClient';
import { GradientContainer } from '../../../../helperComponents/gradients/GradientButton';
import XIcon from '../../../../../resources/svgComponents/XIcon';
import { getTopInset, hitSlop, ProfileBadges } from '../../../../../utils/shared';
import { mbApplyTransparency, mbShadow, mbTextStyles } from '@mightybyte/rnw.utils.style-utils';
import { KeywordView } from '../../../../helperComponents/Keywords';
import { Spacer } from '../../../../helperComponents/Spacer';
import { FONT_STYLES, textStyles } from '../../../../../constants/textStyles';
import { mbGetMediumImage } from '@mightybyte/rnw.components.image';
import Feather from 'react-native-vector-icons/Feather';
import FontAwesome5 from 'react-native-vector-icons/FontAwesome5';
import Fontisto from 'react-native-vector-icons/Fontisto';
import StarIcon from '../../../../../resources/svgComponents/StarIcon';
import SocialXIcon from '../../../../../resources/svgComponents/SocialXIcon';
import FastImage from 'react-native-fast-image';
import { BadgeTypes, UserVisit } from '@sapphicsavvy/business';
import { useTracker } from '../../../../../hooks/useTracker';
import { ForumActivitySvg } from '../../../../../resources/svgComponents/ForumActivitySvg';
import { ArrowRight } from '../../../../../resources/svgComponents/ArrowRight';
import { ForumReputationSvg } from '../../../../../resources/svgComponents/ReputationSvg';
import { ForumPostsCountSvg } from '../../../../../resources/svgComponents/ForumPostsCountSvg';
import EyeIcon from '../../../../../resources/svgComponents/EyeIcon';
import WatchIcon from '../../../../../resources/svgComponents/WatchIcon';

/**
 * Formats a Unix timestamp (in seconds) to relative time (e.g., "2 hours ago", "3 days ago")
 * @param timestamp Unix timestamp in seconds
 * @returns Relative time string
 */
function formatRelativeTime(timestamp: number | undefined): string {
    if (!timestamp || typeof timestamp !== 'number' || isNaN(timestamp)) {
        return '—';
    }

    try {
        const now = Date.now();
        const date = new Date(timestamp * 1000);

        // Check if date is valid
        if (isNaN(date.getTime())) {
            return '—';
        }

        const diffMs = now - date.getTime();
        const diffSeconds = Math.floor(diffMs / 1000);
        const diffMinutes = Math.floor(diffSeconds / 60);
        const diffHours = Math.floor(diffMinutes / 60);
        const diffDays = Math.floor(diffHours / 24);
        const diffMonths = Math.floor(diffDays / 30);
        const diffYears = Math.floor(diffDays / 365);

        if (diffSeconds < 60) {
            return 'Just now';
        } else if (diffMinutes < 60) {
            return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
        } else if (diffHours < 24) {
            return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
        } else if (diffDays < 30) {
            return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
        } else if (diffMonths < 12) {
            return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;
        } else {
            return `${diffYears} year${diffYears !== 1 ? 's' : ''} ago`;
        }
    } catch (error) {
        console.error('Error formatting timestamp:', error);
        return '—';
    }
}

/**
 * Formats a number with K suffix for thousands (e.g., 1000 -> 1K, 5500 -> 5.5K, 15000 -> +10K)
 * @param num Number to format
 * @returns Formatted string
 */
function formatCount(num: number | undefined): string {
    if (num === undefined || num === null) return '0';

    if (num === 0) return '0';

    if (num < 1000) return num.toString();

    if (num >= 10000) return '+10K';

    // For numbers between 1000 and 9999
    const thousands = num / 1000;
    // Remove trailing .0
    const formatted = thousands % 1 === 0
        ? `${Math.floor(thousands)}K`
        : `${thousands.toFixed(1)}K`;

    return formatted;
}

const stars: [number | string, number | string, number][] = [
    ['27%', '4%', 19],
    ['6%', '36%', 35],
    ['9%', '62%', 19],
    ['13%', '92%', 19],
    ['23%', '55%', 57],
    ['50%', '80%', 19],
    ['94%', '4%', 35],
    ['90%', '14%', 19],
    ['76%', '36%', 19],
    ['85%', '62%', 19],
    ['90%', '86%', 35],
];

const renderRandomStars = (
    points: [number | string, number | string, number][],
) => (
    <React.Fragment>
        {points.map((point) => (
            <View
                key={JSON.stringify(point)}
                style={{ position: 'absolute', top: point[1], left: point[0] }}
            >
                <StarIcon size={point[2]} />
            </View>
        ))}
    </React.Fragment>
);

export type SapphicProfileProps = {
    userId?: string;
    onDismiss?: () => void;
    onModalHide?: () => void;
    isLocked?: boolean;
};

const SCREEN_HEIGHT = Dimensions.get('window').height - getTopInset();
const HALF_SCREEN_HEIGHT = SCREEN_HEIGHT / 2;

export default function SapphicProfile({
    userId,
    onDismiss,
    onModalHide,
    isLocked = false,
}: SapphicProfileProps) {
    const { track } = useTracker();

    const { data: userData } = trpc.sapphics.getUserById.useQuery(
        { id: userId as string },
        { enabled: Boolean(userId) },
    );

    const forumProfilePath = userData?.websiteToolboxUserId
        ? `/profile/${userData.websiteToolboxUserId}`
        : undefined;

    const { data: forumSSOData } = trpc.user.getForumSSOUrl.useQuery(
        { redirectPath: forumProfilePath },
        { enabled: Boolean(forumProfilePath) },
    );

    const scrollViewRef = useRef<ScrollView | null>(null);
    const [scrollOffsetY, setScrollOffsetY] = useState(0);
    const calcStyles = useMemo(
        () => getCalcStyles(scrollOffsetY),
        [scrollOffsetY],
    );

    // Track profile view when modal opens
    useEffect(() => {
        if (userId) {
            track(UserVisit.socialize);
        }
    }, [userId, track]);

    const onBackdrop = useCallback(
        () => scrollViewRef.current?.scrollTo({ y: 0, animated: true }),
        [],
    );
    const onScroll = useCallback(
        (e: NativeSyntheticEvent<NativeScrollEvent>) =>
            setScrollOffsetY(e.nativeEvent.contentOffset.y),
        [],
    );

    const tags: string[] = useMemo(() => {
        if (userData) {
            return [...(userData.defaultTags || []), ...(userData.tags || [])];
        }
        return new Array(3).fill('loading');
    }, [userData]);

    const twitter = userData?.socialMedia?.twitter;
    const tiktok = userData?.socialMedia?.tiktok;
    const instagram = userData?.socialMedia?.instagram;

    const hasTwitter = Boolean(twitter);
    const hasTiktok = Boolean(tiktok);
    const hasInstagram = Boolean(instagram);

    const { badgesFirstRow, badgesSecondRow, hasBadges } = useMemo(() => {
        const userBadges: Record<string, number> = userData?.badges || {};
        const entries = Object.entries(userBadges).filter(
            ([badgeType, count]) => count > 0 && ProfileBadges[badgeType as BadgeTypes],
        ) as [BadgeTypes, number][];
        return {
            badgesFirstRow: entries.slice(0, 3),
            badgesSecondRow: entries.slice(3, 6),
            hasBadges: entries.length > 0,
        };
    }, [userData?.badges]);

    return (
        <MB_Modal
            isVisible={userId !== undefined}
            onDismiss={() => onDismiss?.()}
            hideCloseButton
            childrenWrapperStyle={styles.container}
            withoutSafeArea
            statusBarTranslucent
            withoutScrollView
            onModalHide={onModalHide}
        >
            <GradientContainer style={styles.content} angle={-90}>
                <View
                    style={[
                        styles.section,
                        styles.sectionTop,
                        calcStyles.sectionTop,
                    ]}
                >
                    {renderRandomStars(stars)}
                    <View style={styles.imageContainer}>
                        <FastImage
                            style={styles.image}
                            source={{
                                uri: mbGetMediumImage(userData?.avatarUrl),
                            }}
                        />
                    </View>
                    <Text style={styles.username}>
                        {userData?.userName?.toUpperCase() ?? 'Loading...'}
                    </Text>
                    <Text style={styles.location}>
                        {userData?.location?.toUpperCase() ?? ''}
                    </Text>
                    {(hasTwitter || hasTiktok || hasInstagram) && (
                        <Spacer height={10} />
                    )}
                    <View style={styles.social}>
                        {hasTwitter && (
                            <TouchableOpacity
                                style={styles.socialItem}
                                onPress={() =>
                                    Linking.openURL(
                                        `https://www.x.com/${twitter}`,
                                    )
                                }
                            >
                                <SocialXIcon
                                    size={30}
                                    background="transparent"
                                />
                            </TouchableOpacity>
                        )}
                        {hasTiktok && (
                            <TouchableOpacity
                                style={styles.socialItem}
                                onPress={() =>
                                    Linking.openURL(
                                        `https://www.tiktok.com/@${tiktok}`,
                                    )
                                }
                            >
                                <FontAwesome5
                                    name="tiktok"
                                    size={21}
                                    color="#FFFFFF"
                                />
                            </TouchableOpacity>
                        )}
                        {hasInstagram && (
                            <TouchableOpacity
                                style={styles.socialItem}
                                onPress={() =>
                                    Linking.openURL(
                                        `https://www.instagram.com/${instagram}`,
                                    )
                                }
                            >
                                <Feather
                                    name="instagram"
                                    size={21}
                                    color="#FFFFFF"
                                />
                            </TouchableOpacity>
                        )}
                    </View>
                    <TouchableOpacity
                        hitSlop={hitSlop}
                        onPress={() => onDismiss?.()}
                        style={styles.close}
                    >
                        <XIcon size={20} color="#FFFFFF" />
                    </TouchableOpacity>
                </View>
                <View style={styles.section} />
                <ScrollView
                    ref={scrollViewRef}
                    style={[styles.scrollSection]}
                    contentContainerStyle={styles.sectionBottomScrollContent}
                    showsVerticalScrollIndicator={false}
                    onScroll={onScroll}
                    scrollEventThrottle={10}
                >
                    <TouchableOpacity
                        style={[styles.backdrop, calcStyles.backdrop]}
                        onPress={onBackdrop}
                        activeOpacity={1}
                    />
                    <View
                        style={[
                            styles.sectionBottomContent,
                            hasBadges
                                ? styles.sectionBottomContentWithBadges
                                : styles.sectionBottomContentNoBadges,
                        ]}
                    >
                        <View
                            style={[styles.badges, styles.badgesWithAbsolute]}
                        >
                            {badgesFirstRow.map(([badge, count]) => (
                                <Badge key={badge} type={badge} count={count} />
                            ))}
                        </View>
                        <View
                            style={[
                                styles.badges,
                                badgesSecondRow.length > 0 &&
                                    styles.secondBadgesWithAbsolute,
                            ]}
                        >
                            {badgesSecondRow.map(([badge, count]) => (
                                <Badge key={badge} type={badge} count={count} />
                            ))}
                        </View>
                        <View style={styles.tags}>
                            {tags.map((item, index) => (
                                <KeywordView
                                    key={`${item}-${index}`}
                                    value={item}
                                    style={styles.tag}
                                />
                            ))}
                        </View>

                        {(userData?.lastPostTimestamp ||
                            userData?.lastVisitTimestamp ||
                            userData?.postCount !== undefined ||
                            userData?.reputation !== undefined) && (
                            <>
                                <Spacer height={1} />
                                <View style={styles.forumActivity}>
                                    <View style={styles.forumActivityInner}>
                                        <View style={{flexDirection: 'row', marginBottom: 8, justifyContent: 'space-between'}}>
                                            <View style={{flexDirection: 'row',  alignItems: 'center'}}>
                                                <ForumActivitySvg containerStyle={{marginRight: 6}} size={24}/>
                                                <Text style={styles.forumActivityTitleText}>

                                                    Forum Activity
                                                </Text>
                                            </View>
                                            {userData?.websiteToolboxUserId && forumSSOData?.url && (
                                                <TouchableOpacity
                                                    style={[
                                                        styles.viewForum,
                                                    ]}
                                                    onPress={() => {
                                                        if (isLocked) return;
                                                        track(UserVisit.socialize);
                                                        Linking.openURL(forumSSOData.url);
                                                    }}
                                                    disabled={isLocked}
                                                >
                                                    <Text style={[
                                                        styles.forumActivityTitleText,
                                                        {fontSize: 16, marginRight: 10},
                                                        isLocked && styles.viewForumTextLocked,
                                                    ]}>
                                                        View
                                                    </Text>
                                                    {isLocked ? <Fontisto
                                                        name="locked"
                                                        color={'#F14828'}
                                                        size={20}
                                                        style={{ top: -2 }}
                                                    /> : (
                                                        <ArrowRight
                                                            size={8}
                                                            color="#F14828"
                                                        />
                                                    )}
                                                </TouchableOpacity>
                                            )}

                                        </View>
                                        <View style={styles.totalsRow}>
                                            <View style={styles.totalBox}>
                                                <View style={{flexDirection: 'row',  alignItems: 'center'}}>
                                                    <ForumPostsCountSvg containerStyle={{marginRight: 6}}/>
                                                    <Text style={styles.totalLabelText}>
                                                        Total Posts
                                                    </Text>
                                                </View>
                                                <Text style={styles.totalCount}>
                                                    {/*{formatCount(150000)}*/}
                                                    {formatCount(userData?.postCount)}
                                                </Text>
                                            </View>
                                            <View style={styles.totalBox}>
                                                <View style={{flexDirection: 'row',  alignItems: 'center'}}>
                                                    <ForumReputationSvg containerStyle={{marginRight: 6}}/>
                                                    <Text style={styles.totalLabelText}>
                                                        Reputation
                                                    </Text>
                                                </View>
                                                <Text style={styles.totalCount}>
                                                    {/*{formatCount(150)}*/}
                                                    {formatCount(userData?.reputation)}
                                                </Text>
                                            </View>
                                            <View style={styles.totalsRow}>
                                                <View style={styles.totalBox}>
                                                    <View style={{flexDirection: 'row',  alignItems: 'center'}}>
                                                        <Feather name={'message-circle'} color={'#F35E2E'} size={24} />
                                                        <Text style={[styles.totalLabelText, {marginLeft: 6}]}>
                                                            Messages
                                                        </Text>
                                                    </View>
                                                    <Text style={[styles.totalCount, {
                                                        color: userData?.enableMessages ? '#F35E2E' : '#646464'
                                                    }]}>
                                                        {userData?.enableMessages ? 'Enabled' : 'Disabled'}
                                                    </Text>
                                                </View>
                                            </View>
                                        </View>
                                                <View style={styles.forumActivityRow}>
                                                    <View style={{flexDirection: 'row',  alignItems: 'center'}}>
                                                        <EyeIcon containerStyle={{marginRight: 4}} size={18}/>
                                                        <Text style={styles.forumActivityLabel}>
                                                            Last Active:
                                                        </Text>
                                                    </View>
                                                    <Text style={styles.forumActivityValue}>
                                                        {formatRelativeTime(
                                                            userData?.lastVisitTimestamp,
                                                        )}
                                                    </Text>
                                                </View>


                                                    <View style={styles.forumActivityRow}>
                                                        <View style={{flexDirection: 'row',  alignItems: 'center'}}>
                                                            <WatchIcon containerStyle={{marginRight: 4}} size={18}/>
                                                            <Text style={styles.forumActivityLabel}>
                                                                Last Post:
                                                            </Text>
                                                        </View>
                                                        <Text style={styles.forumActivityValue}>
                                                            {formatRelativeTime(
                                                                userData?.lastPostTimestamp,
                                                            )}
                                                        </Text>
                                                    </View>




                                    </View>
                                </View>
                            </>
                        )}
                        <Spacer height={35} />
                        <View style={styles.bio}>
                            <Text style={styles.bioTitleText}>Bio</Text>
                            <Text style={styles.bioText}>{userData?.bio ?? ''}</Text>
                        </View>
                    </View>
                    <View style={styles.scrollSectionBottom} />
                </ScrollView>
            </GradientContainer>
        </MB_Modal>
    );
}

function Badge({ type, count }: { type: BadgeTypes; count: number }) {
    return (
        <View style={styles.badge}>
            {ProfileBadges[type]}
            {count !== undefined && count > 0 && (
                <View style={styles.badgeCount}>
                    <Text style={styles.badgeCountText}>{count}</Text>
                </View>
            )}
        </View>
    );
}

function getCalcStyles(scrollOffsetY: number) {
    return {
        sectionTop: {
            zIndex: scrollOffsetY <= 5 ? 1 : -1,
        },
        backdrop: {
            backgroundColor: mbApplyTransparency(
                '#000000',
                scrollOffsetY <= 5
                    ? 0
                    : (scrollOffsetY * 1.2) / HALF_SCREEN_HEIGHT,
            ),
        },
    };
}

const styles = StyleSheet.create({
    container: {
        backgroundColor: '#f0f',
        marginVertical: 0,
        width: '100%',
        height: '100%',
        paddingHorizontal: 0,
        paddingBottom: 0,
        paddingTop: 0,
    },
    content: {
        width: '100%',
        height: '100%',
        paddingTop: getTopInset(),
    },
    close: {
        position: 'absolute',
        right: 28,
        top: 20,
    },
    section: {
        flex: 1,
    },
    sectionTop: {
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1,
    },
    imageContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 2,
        borderRadius: 80,
        borderColor: '#FFFFFF',
        alignSelf: 'center',
    },
    image: {
        width: 158,
        height: 158,
        borderRadius: 80,
    },
    username: mbTextStyles([
        {
            fontFamily: FONT_STYLES.Tabasco_400,
            fontSize: 35,
            textAlign: 'center',
            color: '#FFFFFF',
            marginTop: 10,
        },
    ]),

    location: mbTextStyles([
        {
            fontFamily: FONT_STYLES.Tabasco_400,
            fontSize: 22,
            textAlign: 'center',
            color: '#FFFFFF',
        },
    ]),
    social: {
        flexDirection: 'row',
        justifyContent: 'center',
    },
    socialItem: {
        backgroundColor: '#EE3823',
        width: 40,
        height: 40,
        borderRadius: 5,
        alignItems: 'center',
        justifyContent: 'center',
        marginHorizontal: 8,
    },
    socialItemLeft: {
        marginLeft: 19,
    },
    socialItemRight: {
        marginRight: 19,
    },
    scrollSection: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
    scrollSectionBottom: {
        position: 'absolute',
        bottom: -1000,
        left: 0,
        right: 0,
        height: 1000,
        backgroundColor: '#EEEEF2',
    },
    sectionBottomScrollContent: {
        paddingTop: getTopInset() + HALF_SCREEN_HEIGHT - 18,
        flexGrow: 1,
    },
    backdrop: {
        backgroundColor: '#000000',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: -1,
        position: 'absolute',
    },
    sectionBottomContent: {
        borderTopLeftRadius: 36,
        borderTopRightRadius: 36,
        backgroundColor: '#EEEEF2',
        paddingVertical: 40,
        flexGrow: 1,
        ...mbShadow({
            offsetWidth: -5,
            offsetHeight: 0,
            color: '#000000',
            opacity: 0.15,
            radius: 10,
            elevation: 2,
        }),
    },
    sectionBottomContentWithBadges: {
        backgroundColor: '#EEEEF1',
    },
    sectionBottomContentNoBadges: {
        paddingTop: 0,
        overflow: 'hidden',
    },
    badges: {
        flexDirection: 'row',
        justifyContent: 'center',
    },
    badgesWithAbsolute: {
        position: 'absolute',
        top: -28,
        alignSelf: 'center',
        zIndex: 1,
    },
    secondBadgesWithAbsolute: {
        marginBottom: -25,
        zIndex: 2,
    },
    badge: {
        width: 59,
        height: 59,
        backgroundColor: '#85249d',
        borderWidth: 5,
        borderColor: '#e5a800',
        marginHorizontal: 9,
        borderRadius: 360,
        alignItems: 'center',
        justifyContent: 'center',
    },
    badgeCount: {
        borderRadius: 360,
        backgroundColor: '#EE3823',
        position: 'absolute',
        height: 20,
        width: 20,
        right: -7,
        top: -7,
        alignItems: 'center',
        justifyContent: 'center',
    },
    badgeCountText: mbTextStyles(textStyles.smallerText),
    tags: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'center',
        // backgroundColor: '#FFF0EF',
        paddingVertical: 14,
        paddingHorizontal: 27,
        paddingBottom: 14 + 21,
    },
    tag: {
        marginRight: 17,
        marginTop: 21,
        backgroundColor: '#FFF0EF',
    },
    bio: {
        paddingHorizontal: 30,
    },
    bioTitleText: mbTextStyles([
        textStyles.normalText,
        {
            fontSize: 18,
            fontWeight: '600',
            color: '#F14828',
            textAlign: 'left',
        },
    ]),
    bioText: mbTextStyles([
        textStyles.smallText,
        {
            fontWeight: '400',
            color: '#000000',
            textAlign: 'left',
        },
    ]),
    forumActivity: {
        paddingHorizontal: 30,
    },
    forumActivityInner: {
        padding: 16,
        backgroundColor: '#FFF0EF',
        borderColor: '#F14828',
        borderWidth: 1,
        borderRadius: 16,
    },
    forumActivityTitleText: mbTextStyles([
        textStyles.normalText,
        {
            fontSize: 18,
            fontWeight: '600',
            color: '#F14828',
            textAlign: 'left',
        },
    ]),
    forumActivityContent: {
        // gap: 8, // Using marginBottom on rows instead for RN compatibility
    },
    viewForum: {
        backgroundColor: '#FFFFFF',
        borderRadius:45,
        paddingVertical: 10,
        paddingHorizontal: 10,
        flexDirection:'row',
        justifyContent:'center',
        alignItems: 'center',
    },
    viewForumLocked: {
        opacity: 0.5,
    },
    viewForumTextLocked: {
        color: mbApplyTransparency('#F14828', 0.4),
    },
    totalsRow: {
        marginBottom: 8,
    },
    totalBox: {
        backgroundColor: '#FFFFFF98',
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 8,
        borderColor: '#F35E2E52',
        borderWidth: 1,
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 8,
    },
    totalLabelText: mbTextStyles([
        textStyles.normalText, {
            fontSize: 15,
            fontWeight: '400',
            color: '#000000',
        }
    ]),
    totalCount: mbTextStyles([
        textStyles.normalText, {
            fontSize: 17,
            fontWeight: '500',
            color: '#F14828'
        }
    ]),
    forumActivityRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8,
    },
    forumActivityLabel: mbTextStyles([
        textStyles.smallText,
        {
            fontWeight: '400',
            fontSize: 14,
            color: '#646464',
            textAlign: 'left',
        },
    ]),
    forumActivityValue: mbTextStyles([
        textStyles.smallText,
        {
            fontWeight: '400',
            fontSize: 14,
            color: '#646464',
            textAlign: 'right',
            flex: 1,
            marginLeft: 10,
        },
    ]),
});
